import {
  type ActionResult,
  type ActivityEntry,
  AI_SETTING_LABELS,
  type AiOverview,
  type AiSettings,
  type ConnectedApp,
} from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, api, copyText, del, patch, post, timeAgo } from "../api";
import { Icon } from "../components/Icons";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

const ORDER: (keyof AiSettings)[] = [
  "findPosts",
  "approveWithinRules",
  "tagProducts",
  "createSchedule",
  "draftMessages",
  "acceptClearYes",
  "publishWithoutApproval",
];

const NOTE: Partial<Record<keyof AiSettings, string>> = {
  createSchedule: "needs your approval",
  acceptClearYes: "off by default",
  publishWithoutApproval: "off by default",
};

const CAN_USE = [
  "Find posts",
  "Score posts",
  "Approve and skip",
  "Tag products",
  "Create walls",
  "Schedule walls",
  "Ask for permission",
  "Read insights",
];

function ApiKeys({ apps }: { apps: ConnectedApp[] }) {
  const { ws } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [key, setKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    try {
      const r = await post<{ key: string }>(`/v1/w/${ws}/api-keys`, { name });
      setKey(r.key);
      setName("");
      qc.invalidateQueries({ queryKey: ["ai", ws] });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  return (
    <section className="card stack">
      <div>
        <h2>API keys</h2>
        <p className="muted">
          Connect your own tools and scripts. A key can do what you can, within the switches above.
        </p>
      </div>
      {error && <div className="error">{error}</div>}
      {key && (
        <div className="notice stack" style={{ gap: 8 }}>
          <b>Copy your key now. You won't see it again.</b>
          <code className="code" style={{ background: "#0d2722" }}>
            {key}
          </code>
          <button
            type="button"
            className="btn sm"
            onClick={async () => {
              await copyText(key);
              toast("Copied.");
            }}
          >
            Copy
          </button>
        </div>
      )}
      {apps
        .filter((a) => a.kind === "api_key")
        .map((a) => (
          <div key={a.id} className="row">
            <span className="spacer">
              <b>{a.name}</b>{" "}
              <span className="small muted mono">
                {a.prefix}… · {a.lastActiveAt ? `used ${timeAgo(a.lastActiveAt)}` : "never used"}
              </span>
            </span>
            <button
              type="button"
              className="btn link small"
              onClick={async () => {
                const r = await del<ActionResult>(`/v1/w/${ws}/ai/apps/${a.id}`);
                toast(r.summary);
                qc.invalidateQueries({ queryKey: ["ai", ws] });
              }}
            >
              Revoke
            </button>
          </div>
        ))}
      <div className="row">
        <input
          className="input spacer"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name, for example Stock script"
          aria-label="Key name"
        />
        <button type="button" className="btn secondary" disabled={!name.trim()} onClick={create}>
          Create key
        </button>
      </div>
    </section>
  );
}

export function Ai() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const qc = useQueryClient();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const overview = useQuery({
    queryKey: ["ai", ws],
    queryFn: () => api<AiOverview>(`/v1/w/${ws}/ai`),
  });
  const log = useQuery({
    queryKey: ["activity", ws, "ai"],
    queryFn: () =>
      api<{ entries: ActivityEntry[]; waiting: number }>(`/v1/w/${ws}/activity?ai=true&limit=40`),
  });
  const isOwner = workspace.role === "owner";
  const canAct = workspace.role !== "viewer";

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["ai", ws] });
    qc.invalidateQueries({ queryKey: ["activity", ws] });
  };
  const run = async (fn: () => Promise<ActionResult>) => {
    try {
      toast((await fn()).summary);
      refresh();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const set = async (k: keyof AiSettings, v: boolean) => {
    qc.setQueryData(["ai", ws], {
      ...overview.data!,
      settings: { ...overview.data!.settings, [k]: v },
    });
    await patch(`/v1/w/${ws}/ai/settings`, { [k]: v });
    refresh();
  };

  const o = overview.data;
  if (!o) return <p className="muted">Loading…</p>;
  const waiting = o.approvals.filter((a) => a.status === "waiting");
  const mcpApps = o.apps.filter((a) => a.kind === "mcp");

  return (
    <div className="stack" style={{ gap: 18 }}>
      {!o.allowed && (
        <div className="card row wrap">
          <p className="spacer">
            <b>Your own AI is part of Pro.</b> Let Claude, ChatGPT or another AI app do the work in
            Brandwall for you, from the chat you already use.
          </p>
          <Link className="btn" to={`/w/${ws}/settings?tab=plan`}>
            Start a free trial
          </Link>
        </div>
      )}
      <div className="grid2" style={{ alignItems: "start" }}>
        <div className="stack">
          <section className="card stack">
            <div>
              <h2>Connect your own AI</h2>
              <p className="muted">
                Chat with Claude, ChatGPT or another AI app, and it works in Brandwall for you. Add
                this link to your AI app as a connector. Any app that supports MCP can use it.
              </p>
            </div>
            <div className="row">
              <input
                className="input mono spacer"
                readOnly
                value={o.mcpUrl}
                aria-label="Connection link"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className={`btn ${copied ? "ok" : ""}`}
                onClick={async () => {
                  await copyText(o.mcpUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? "✓ Copied" : "Copy"}
              </button>
            </div>
            <p className="small muted">
              The link holds no password, so it's safe to paste. Your AI app asks you to log in to
              Brandwall once, and you choose which brands it may use.
            </p>
          </section>

          <section className="card stack">
            <h2>What your AI may do</h2>
            {ORDER.map((k) => (
              <div key={k} className="row">
                <span className="spacer" style={{ fontWeight: 600 }}>
                  {AI_SETTING_LABELS[k]}
                  {NOTE[k] && (
                    <span className="small muted" style={{ fontWeight: 500 }}>
                      {" "}
                      · {NOTE[k]}
                    </span>
                  )}
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={o.settings[k]}
                    disabled={!isOwner}
                    aria-label={AI_SETTING_LABELS[k]}
                    onChange={(e) => set(k, e.target.checked)}
                  />
                  <span />
                </label>
              </div>
            ))}
            <p className="small muted">
              When your AI asks creators for permission, Brandwall writes the message and the
              creator's personal link. Your AI can never delete posts or walls, change billing,
              invite people or change these switches.
            </p>
          </section>

          <section className="card stack" id="approvals">
            <div className="row">
              <h2 className="spacer">Waiting for your approval</h2>
              {waiting.length > 0 && <span className="pill berry">{waiting.length}</span>}
            </div>
            {waiting.length === 0 && <p className="muted">Nothing is waiting for you.</p>}
            {waiting.map((a) => (
              <div
                key={a.id}
                className="stack"
                style={{ gap: 6, paddingBottom: 10, borderBottom: "1px solid var(--line)" }}
              >
                <b>{a.summary}</b>
                <span className="small muted">
                  {a.requestedBy} · {timeAgo(a.createdAt)}
                  {a.reason ? ` · "${a.reason}"` : ""}
                </span>
                {canAct && (
                  <div className="row">
                    <button
                      type="button"
                      className="btn ok sm"
                      onClick={() => run(() => post(`/v1/w/${ws}/approvals/${a.id}/approve`, {}))}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn secondary sm"
                      onClick={() => run(() => post(`/v1/w/${ws}/approvals/${a.id}/reject`, {}))}
                    >
                      Turn down
                    </button>
                  </div>
                )}
              </div>
            ))}
          </section>
        </div>

        <div className="stack">
          <section className="card stack">
            <h2>Connected AI apps</h2>
            {mcpApps.length === 0 && <p className="muted">No AI app is connected yet.</p>}
            {mcpApps.map((a) => (
              <div key={a.id} className="row">
                <span className="av" style={{ background: "var(--ink)" }}>
                  {Icon.ai}
                </span>
                <span className="spacer">
                  <b>{a.name}</b>
                  <div className="small muted">
                    Connected{a.createdBy ? ` by ${a.createdBy}` : ""} ·{" "}
                    {a.lastActiveAt ? `last active ${timeAgo(a.lastActiveAt)}` : "not used yet"}
                  </div>
                </span>
                <span className="pill ok">
                  <span className="dot" /> Connected
                </span>
                {isOwner && (
                  <button
                    type="button"
                    className="btn link small"
                    onClick={() => {
                      if (
                        window.confirm(
                          `Disconnect ${a.name}? It stops working for this brand at once.`,
                        )
                      )
                        run(() => del(`/v1/w/${ws}/ai/apps/${a.id}`));
                    }}
                  >
                    Disconnect
                  </button>
                )}
              </div>
            ))}
          </section>

          <section className="card stack">
            <h2>How it works</h2>
            <ol className="steps-list">
              <li>Copy the link above and add it as a connector in your AI app.</li>
              <li>Ask your AI in plain words, in the app you already use.</li>
              <li>
                Your AI does the work in Brandwall. You see every step here, and approve the big
                ones.
              </li>
            </ol>
            <p className="quote small">
              The chat happens in your own AI app, like Claude or ChatGPT. Brandwall has no chat of
              its own.
            </p>
          </section>

          <section className="card stack">
            <h2>What your AI can use</h2>
            <div className="chips">
              {CAN_USE.map((c) => (
                <span key={c} className="chip">
                  {c}
                </span>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section className="card stack">
        <div className="row">
          <h2 className="spacer">Activity log</h2>
          <span className="small muted">Everything your AI did in Brandwall</span>
        </div>
        {log.data?.entries.length === 0 && <p className="muted">Nothing yet.</p>}
        <div>
          {log.data?.entries.map((e) => (
            <div
              key={e.id}
              className="row"
              style={{
                padding: "10px 0",
                borderBottom: "1px solid var(--line)",
                alignItems: "flex-start",
              }}
            >
              <span className="small muted mono" style={{ width: 92, flex: "none" }}>
                {new Date(e.createdAt).toLocaleString("en-GB", {
                  weekday: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              <span className="spacer">
                <b>{e.app ?? e.actor}</b> · {e.summary}
                {e.reason && <span className="small muted"> · "{e.reason}"</span>}
              </span>
              {e.tool && <span className="small muted mono hide-sm">{e.tool}</span>}
              {e.approval?.status === "approved" && (
                <span className="pill ok">
                  ✓ Approved{e.approval.decidedBy ? ` by ${e.approval.decidedBy}` : ""}
                </span>
              )}
              {e.approval?.status === "waiting" && <span className="pill warn">Waiting</span>}
              {e.undone ? (
                <span className="pill">✓ Undone</span>
              ) : (
                e.canUndo &&
                canAct && (
                  <button
                    type="button"
                    className="btn secondary sm"
                    onClick={() => run(() => post(`/v1/w/${ws}/activity/${e.id}/undo`, {}))}
                  >
                    Undo
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </section>

      {isOwner && o.allowed && <ApiKeys apps={o.apps} />}
    </div>
  );
}
